import React from 'react'; // Add this import
import PropTypes from 'prop-types';
import isString from 'lodash/isString';
import { m, AnimatePresence } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, List, Stack, Button, IconButton, ListItemText, ListItem } from '@mui/material';
// utils
import { fData } from '../../utils/formatNumber';
//
import Image from '../Image';
import Iconify from '../Iconify';
import { varFade } from '../animate';

// ----------------------------------------------------------------------

const getFileData = (file) => {
  if (typeof file === 'string') {
    return {
      key: file,
    };
  }
  return {
    key: `${file.name}:${file.size}:${file.lastModified}`,
    name: file.name,
    size: file.size,
    preview: file.preview,
  };
};

// ----------------------------------------------------------------------

MultiFilePreview.propTypes = {
  files: PropTypes.array.isRequired,
  showPreview: PropTypes.bool,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  onMove: PropTypes.func,
};

MultiFilePreview.defaultProps = {
  showPreview: false,
  // files: [],
};

// Arrow buttons and drag-and-drop call onMove(fromIndex, toIndex), where the
// indexes are positions in `files`. Without onMove the list can't be reordered.
export default function MultiFilePreview({ showPreview, files, onRemove, onRemoveAll, onMove }) {
  const hasFile = files && files.length > 0;
  const [dragIndex, setDragIndex] = React.useState(null);
  const count = files ? files.length : 0;

  // Keys follow the image, not its position: a position-based key remounts
  // every tile on reorder, and AnimatePresence keeps the old tiles around
  // while they animate out, so a quick second click could hit a stale tile.
  const seen = {};
  const keyFor = (file) => {
    const { key } = getFileData(file);
    seen[key] = (seen[key] || 0) + 1;
    return seen[key] > 1 ? `${key}#${seen[key]}` : key;
  };

  const dragProps = (index) =>
    onMove
      ? {
          draggable: true,
          onDragStart: (e) => {
            setDragIndex(index);
            e.dataTransfer.effectAllowed = 'move';
          },
          onDragOver: (e) => e.preventDefault(),
          onDrop: (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (dragIndex !== null && dragIndex !== index) onMove(dragIndex, index);
            setDragIndex(null);
          },
          onDragEnd: () => setDragIndex(null),
        }
      : {};

  const moveButton = (index, delta, icon, label) => (
    <IconButton
      size="small"
      aria-label={label}
      disabled={index + delta < 0 || index + delta >= count}
      onClick={() => onMove(index, index + delta)}
      sx={{
        p: '2px',
        color: 'common.white',
        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
        '&:hover': { bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48) },
        '&.Mui-disabled': { display: 'none' },
      }}
    >
      <Iconify icon={icon} />
    </IconButton>
  );

  return (
    <>
      <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
      <AnimatePresence>
      {files && files.map((file, index) => {
            // Skip empty slots without shifting indexes, so onMove/onRemove
            // positions match the form's array.
            if (file == null) return null;
            const { name, size, preview } = getFileData(file);
            const key = keyFor(file);

            if (showPreview) {
              return (
                <ListItem
                  key={key}
                  component={m.div}
                  {...varFade().inRight}
                  {...dragProps(index)}
                  sx={{
                    p: 0,
                    m: 0.5,
                    width: 80,
                    height: 80,
                    ...(onMove && { cursor: 'grab' }),
                    ...(dragIndex === index && { opacity: 0.4 }),
                    borderRadius: 1.25,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'inline-flex',
                    border: (theme) => `solid 1px ${theme.palette.divider}`,
                  }}
                >
                  <Image alt="preview" src={isString(file) ? file : preview} ratio="1/1" />
                  {onMove && (
                    <>
                      <Box
                        sx={{
                          top: 6,
                          left: 6,
                          px: 0.75,
                          borderRadius: 0.75,
                          position: 'absolute',
                          typography: 'caption',
                          color: 'common.white',
                          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', bottom: 6, left: 6 }}>
                        {moveButton(index, -1, 'eva:arrow-back-fill', 'Move earlier')}
                        {moveButton(index, 1, 'eva:arrow-forward-fill', 'Move later')}
                      </Stack>
                    </>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => onRemove(file, index)}
                    sx={{
                      top: 6,
                      p: '2px',
                      right: 6,
                      position: 'absolute',
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                      },
                    }}
                  >
                    <Iconify icon={'eva:close-fill'} />
                  </IconButton>
                </ListItem>
              );
            }

            return (
              <ListItem
                key={key}
                component={m.div}
                {...varFade().inRight}
                {...dragProps(index)}
                sx={{
                  my: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 0.75,
                  border: (theme) => `solid 1px ${theme.palette.divider}`,
                }}
              >
                <Iconify icon={'eva:file-fill'} sx={{ width: 28, height: 28, color: 'text.secondary', mr: 2 }} />

                <ListItemText
                  primary={isString(file) ? file : name}
                  secondary={isString(file) ? '' : fData(size || 0)}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />

                {onMove && (
                  <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
                    {moveButton(index, -1, 'eva:arrow-upward-fill', 'Move earlier')}
                    {moveButton(index, 1, 'eva:arrow-downward-fill', 'Move later')}
                  </Stack>
                )}
                <IconButton edge="end" size="small" onClick={() => onRemove(file, index)}>
                  <Iconify icon={'eva:close-fill'} />
                </IconButton>
              </ListItem>
            );
          })}
        </AnimatePresence>
      </List>

      {/* {hasFile && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button color="inherit" size="small" onClick={onRemoveAll}>
            Remove
          </Button>
          <Button size="small" variant="contained">
            Upload files
          </Button>
        </Stack>
      )} */}
    </>
  );
}
